import { buildEscPosReceipt, buildKitchenTicketsList, ReceiptOrder } from "./receiptPrint";
import { activeCurrencySymbol } from "../../utils/currency";

// ── Known ESC/POS BLE printer profiles ──────────────────────────────────────
// Different manufacturers use different GATT service UUIDs.
// We probe them in order until one works.
const ESC_POS_PROFILES = [
  {
    // FF00/FF02 — MUNBYN MPT-II/III (most units)
    service: "0000ff00-0000-1000-8000-00805f9b34fb",
    txChar: "0000ff02-0000-1000-8000-00805f9b34fb",
  },
  {
    // FF00/FF01 — MUNBYN MPT-III alternate (some units use ff01 as TX)
    service: "0000ff00-0000-1000-8000-00805f9b34fb",
    txChar: "0000ff01-0000-1000-8000-00805f9b34fb",
  },
  {
    // FFE0 — another very common Chinese printer profile
    service: "0000ffe0-0000-1000-8000-00805f9b34fb",
    txChar: "0000ffe1-0000-1000-8000-00805f9b34fb",
  },
  {
    // Nordic UART — Epson TM-m30/m30II, Star SM-L200/mPOP, Bixolon SPP-R
    service: "6e400001-b5b3-f393-e0a9-e50e24dcca9e",
    txChar: "6e400002-b5b3-f393-e0a9-e50e24dcca9e",
  },
  {
    // 18F0 — some HP/generic portable printers
    service: "000018f0-0000-1000-8000-00805f9b34fb",
    txChar: "00002af1-0000-1000-8000-00805f9b34fb",
  },
];

// All service UUIDs must be in optionalServices so we can access them after connect
const ALL_SERVICES = Array.from(new Set(ESC_POS_PROFILES.map((p) => p.service)));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bt = () => (navigator as any).bluetooth;

// Module-level device caches — survive for the current session only, reset on reload.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cachedDevice: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cachedKitchenDevice: any = null;

export const isBluetoothSupported = (): boolean =>
  typeof navigator !== "undefined" && "bluetooth" in navigator;

/**
 * Returns the in-session paired receipt printer name, or null if not yet paired this session.
 * Resets to null on every page reload (no localStorage persistence).
 */
export const getBluetoothPrinterName = (): string | null =>
  cachedDevice?.name ?? null;

/**
 * Clears the in-session receipt printer cache.
 */
export const clearBluetoothPrinter = (): void => {
  cachedDevice = null;
};

/**
 * Shows the browser Bluetooth device picker.
 * Must be called from a user-gesture handler (button click).
 * Pairs for this session only — on reload, pairing is required again.
 */
export const pairBluetoothPrinter = async (): Promise<string> => {
  if (!isBluetoothSupported()) {
    throw new Error(
      "Web Bluetooth is not supported on this browser. Use Chrome on Android.",
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const device: any = await bt().requestDevice({
    acceptAllDevices: true,
    optionalServices: ALL_SERVICES,
  });

  const name: string = device.name || "Bluetooth Printer";
  cachedDevice = device;
  return name;
};

// ── Kitchen printer (separate device cache) ───────────────────────────────────

/**
 * Returns the in-session paired kitchen printer name, or null if not yet paired this session.
 * Resets to null on every page reload (no localStorage persistence).
 */
export const getKitchenPrinterName = (): string | null =>
  cachedKitchenDevice?.name ?? null;

/**
 * Clears the in-session kitchen printer cache.
 */
export const clearKitchenPrinter = (): void => {
  cachedKitchenDevice = null;
};

/**
 * Shows the Bluetooth device picker so the user can select a kitchen printer.
 * Must be called from a user-gesture handler (button click).
 * Pairs for this session only — on reload, pairing is required again.
 */
export const pairKitchenPrinter = async (): Promise<string> => {
  if (!isBluetoothSupported()) {
    throw new Error(
      "Web Bluetooth is not supported on this browser. Use Chrome on Android.",
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const device: any = await bt().requestDevice({
    acceptAllDevices: true,
    optionalServices: ALL_SERVICES,
  });
  const name: string = device.name || "Kitchen Printer";
  cachedKitchenDevice = device;
  return name;
};

const escPosLinesToBytes = (lines: string[]): Uint8Array => {
  const encoder = new TextEncoder();
  const chunks = lines.map((l) => encoder.encode(l));
  const total = chunks.reduce((s, c) => s + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const findPrintCharacteristic = async (server: any) => {
  for (const profile of ESC_POS_PROFILES) {
    try {
      const service = await server.getPrimaryService(profile.service);
      const char = await service.getCharacteristic(profile.txChar);
      console.log(`[BT] Found ESC/POS profile: ${profile.service}`);
      return char;
    } catch {
      // this profile not available, try next
    }
  }
  throw new Error(
    "No ESC/POS print service found on this device. " +
      "Ensure the printer is a BLE ESC/POS model and is powered on.",
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const writeBytes = async (char: any, bytes: Uint8Array): Promise<void> => {
  const CHUNK = 200;
  const canWriteWithResponse: boolean = !!char.properties?.write;

  for (let i = 0; i < bytes.length; i += CHUNK) {
    const chunk = bytes.slice(i, i + CHUNK);
    if (canWriteWithResponse) {
      await char.writeValueWithResponse(chunk);
    } else {
      await char.writeValueWithoutResponse(chunk);
      await new Promise((r) => setTimeout(r, 20));
    }
  }
};
let printedOrderIds = new Set<string>();


/**
 * Prints a receipt to the paired BLE ESC/POS printer.
 *
 * Uses the in-session cached device. If no device is cached (first print or
 * after reload), shows the browser Bluetooth picker — requires a user gesture.
 */
export const printReceiptBluetooth = async (
  order: ReceiptOrder,
  customerName?: string,
  logoBase64?: string,
  currencySymbol = activeCurrencySymbol(),
): Promise<void> => {
  if (!isBluetoothSupported()) {
    throw new Error("Web Bluetooth not supported");
  }

  // Use in-session cache, or show picker (requires user gesture)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let device: any = cachedDevice;

  if (!device) {
    device = await bt().requestDevice({
      acceptAllDevices: true,
      optionalServices: ALL_SERVICES,
    });
    cachedDevice = device;
  }

  const server = await device.gatt.connect();
  try {
    const characteristic = await findPrintCharacteristic(server);

    const textLines = buildEscPosReceipt(order, customerName, currencySymbol);
    const textBytes = escPosLinesToBytes(textLines);

    let allBytes: Uint8Array;
    if (logoBase64) {
      const logoBytes = base64ToBytes(logoBase64);
      allBytes = new Uint8Array(logoBytes.length + textBytes.length);
      allBytes.set(logoBytes, 0);
      allBytes.set(textBytes, logoBytes.length);
    } else {
      allBytes = textBytes;
    }

    await writeBytes(characteristic, allBytes);
    const characteristics = await findPrintCharacteristic(server);
    const tickets = buildKitchenTicketsList(order);
    for (let i = 0; i < tickets.length; i++) {
      await writeBytes(characteristics, escPosLinesToBytes(tickets[i]));
      const PRINTED_STORAGE_PREFIX = "kitchenPrintedOrders";
          const key = `${PRINTED_STORAGE_PREFIX}:${order.restaurantId}`;
          const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) {
       printedOrderIds = new Set(parsed);
    }
          try {
      printedOrderIds.add(order._id);
      const ids = Array.from(printedOrderIds);
      localStorage.setItem(key, JSON.stringify(ids));
    } catch (err) {
      console.warn("Failed to persist printed orders", err);
    }
      if (i < tickets.length - 1) {
        await new Promise<void>((r) => setTimeout(r, 400));
      }
    }
  } finally {
    device.gatt.disconnect();
  }
};

/**
 * Prints per-item kitchen tickets to the paired kitchen BLE printer.
 *
 * Uses the in-session cached device. If no device is cached (first print or
 * after reload), throws "NO_KITCHEN_PRINTER" so the caller can show the
 * pairing modal (which requires a user gesture to call pairKitchenPrinter).
 */
export const printKitchenTicketsBluetooth = async (
  order: ReceiptOrder,
): Promise<void> => {
  if (!isBluetoothSupported()) {
    throw new Error("Web Bluetooth not supported");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const device: any = cachedKitchenDevice;

  if (!device) {
    // No printer paired this session — caller shows the pairing modal
    throw new Error("NO_KITCHEN_PRINTER");
  }

  const server = await device.gatt.connect();
  try {
    const characteristic = await findPrintCharacteristic(server);
    const tickets = buildKitchenTicketsList(order);
    for (let i = 0; i < tickets.length; i++) {
      await writeBytes(characteristic, escPosLinesToBytes(tickets[i]));
      if (i < tickets.length - 1) {
        await new Promise<void>((r) => setTimeout(r, 400));
      }
    }
  } finally {
    device.gatt.disconnect();
  }
};

/**
 * Prints per-item kitchen tickets via RawBT Android app.
 */
export const printKitchenTicketsRawBT = (order: ReceiptOrder): void => {
  const allLines = buildKitchenTicketsList(order).flat();
  const bytes = escPosLinesToBytes(allLines);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  const base64 = btoa(binary);
  const intentUrl =
    `intent:base64,${base64}` +
    `#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
  const a = document.createElement("a");
  a.href = intentUrl;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Decodes a base64 string to Uint8Array (for ESC/POS logo bytes from server).
 */
const base64ToBytes = (b64: string): Uint8Array => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

/**
 * Prints via RawBT Android app using the rawbt: intent URL scheme.
 */
export const printReceiptRawBT = (
  order: ReceiptOrder,
  customerName?: string,
  logoBase64?: string,
  currencySymbol = activeCurrencySymbol(),
): void => {
  const textLines = buildEscPosReceipt(order, customerName, currencySymbol);
  const textBytes = escPosLinesToBytes(textLines);

  let allBytes: Uint8Array;
  if (logoBase64) {
    const logoBytes = base64ToBytes(logoBase64);
    allBytes = new Uint8Array(logoBytes.length + textBytes.length);
    allBytes.set(logoBytes, 0);
    allBytes.set(textBytes, logoBytes.length);
  } else {
    allBytes = textBytes;
  }

  let binary = "";
  allBytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  const base64 = btoa(binary);

  const intentUrl =
    `intent:base64,${base64}` +
    `#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;

  const a = document.createElement("a");
  a.href = intentUrl;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
